import { Mixer } from "./Mixer.js";
import { DTSInterface } from './DTSInterface.js';
import { Peers_Handler } from "./Peers.js";

import { OutputConfig, BuildSettings } from "./OutputConfig.js"
import { Logger } from "./Logger.js";
import { get } from "svelte/store";
import { playback, core_init_ok, core_init_failed, decoder_thread_ready, playback_ctx_resume_ok, pluginUpdateAvailable, awaiting_keyframe, connection_status, duplicateEngAlert } from "./stores.js";

import { delay } from "./Utils.js";

// -- one eng per room (pl-ws claim_eng_slot). "Use this tab instead" sets a one-shot takeover
// -- flag and reloads (URL hash if site storage is blocked); pagehide stores this page's id so
// -- this tab's next load can name its own earlier pages (prev), which pl-ws may replace even
// -- when that socket still looks live (half-open after a network change)
const ENG_TAKEOVER_KEY = "pl-eng-takeover";
const ENG_TAKEOVER_HASH = "#pl-eng-takeover";
const ENG_PREV_KEY = "pl-eng-prev-id";

// -- This is just a central place for interfacing with the decoder thread
export const Dispatcher = {

    decoderThread: null,
    use_expanded_io: false,
    self_id: null, // -- client-minted peer id (8 hex chars), created in initDecoderThread
    dts_canvases: new Map(), // id → HTMLCanvasElement (pre-offscreen-transfer, for UI use)

    setEngConfig(){
        
        console.assert(this.decoderThread);
        this.decoderThread.postMessage({route: "local_video_rendering_ui", value: OutputConfig.local_video_rendering});        
        this.decoderThread.postMessage({route: "set_low_vStream_quality", value: OutputConfig.video_bitrate === "low"});
        
        //Tabling all this for now
        //this.decoderThread && this.decoderThread.postMessage({route: "set_force_vStream", value: OutputConfig.force_vStream});
        // -- this could be a UI switch but probably not
        //this.decoderThread && this.decoderThread.postMessage({route: "set_disable_simulcast", value: BuildSettings.DISABLE_SIMULCAST});
        //this.decoderThread && this.decoderThread.postMessage({route: "set_disable_lcl_video_render", value: OutputConfig.disable_lcl_video_render});
        //we need to notify all peers if we are disabling conf auto-pause
        //maybe we make this a conference event?
    },

    // -- never reset: a resumed context stays gestured for the page's life
    _ctx_resumed: false,

    // -- Only the gesture-critical resume() lives here; the thread-facing half is deferred to
    // -- _notifyCtxResumed because the start panel's OK/Join tap can beat initDecoderThread
    // -- (App.onMount is still awaiting the AWP worklet module load when a prefilled-name tap
    // -- lands). The old code posted to a null decoderThread, the .catch swallowed the
    // -- TypeError, and playback_ctx_resume_ok -- this store's only writer -- was never set,
    // -- leaving Join stuck on "Loading" forever.
    resumeAudioContextFirstTime(){

        Mixer.audioContext.resume().then(() => {
            console.log("[PL-Dispatcher] Resume Audio Context OK");
            this._ctx_resumed = true;
            this._notifyCtxResumed();
        }).catch(e => {
            console.error("[PL-Dispatcher] Audio Context Resume Failed", e);
        });
    },

    // -- needs both a resumed context and a live thread; called from both sides of the race so
    // -- whichever finishes last delivers (initDecoderThread replays it, like signaling-resynced)
    _notifyCtxResumed(){

        if (!this._ctx_resumed)
            return;

        if (!this.decoderThread){
            console.warn("[PL-Dispatcher] Resume beat initDecoderThread -- will replay");
            return;
        }

        this.decoderThread.postMessage({route: "audio-context-resume", value: true});
        Mixer.reassessOutputLatency("ctx-resume");
        playback_ctx_resume_ok.set(true);
    },

    async initAudioContext(){
        return await Mixer.init(ahl => Dispatcher.notifyAudioHardwareLatency(ahl));
    },

    // -- Mixer can measure this before initDecoderThread has run (its first reassess fires from
    // -- inside Mixer.init, one await earlier), so keep the value and replay it with the thread.
    _last_ahl: null,

    notifyAudioHardwareLatency(ahl){
        this._last_ahl = ahl;
        this.decoderThread && this.decoderThread.postMessage({route: "audio_hardware_latency", audio_hardware_latency: ahl});
    },

    updatePlaybackCanvasSize(height, width){
        this.decoderThread && this.decoderThread.postMessage({route: "resize", resize: {height: Math.round(height), width: Math.round(width)}})
    },

    transferPlaybackCanvas(canvas){
        if (!this.decoderThread) return false;

        const offscreen = canvas.transferControlToOffscreen();
        this.decoderThread.postMessage({route: "transfer_playback_canvas", canvas: offscreen}, [offscreen]);
        return true;
    },


    transferDtsCanvas(id, canvas) {
        const offscreen = canvas.transferControlToOffscreen();
        this.decoderThread.postMessage({ route: 'transfer_dts_canvas', id: id, canvas: offscreen }, [offscreen]);
    },

    // -- unused for now --
    // setExpandedIO(use_expanded_io){
    //     this.use_expanded_io = use_expanded_io; //we need a local copy (that is not a store) here so that talent metering can be correctly set
    //     this.decoderThread && this.decoderThread.postMessage({
    //         route: "set_expanded_io",
    //         state: use_expanded_io
    //     })
    // },

    // -- first Join: lets the worker-side Relays start pushing media (eng's plugin stream is
    // -- otherwise dropped pre-join, see Relays_v2.can_transmit)
    setCanTransmit(value){
        this.decoderThread && this.decoderThread.postMessage({route: "set_can_transmit", value: value});
    },

    // -- make this one message
    notifyCanRender(id, value){
        this.decoderThread && this.decoderThread.postMessage({route: "notify_can_render", value: value, id: id});
    },

    notifyNameChange(id, name){
        this.decoderThread && this.decoderThread.postMessage({route: "notify_name_change", id: id, name: name});
    },

    // -- every platform: the renderer stops painting a page nobody can see
    setPageHidden(hidden){
        this.decoderThread && this.decoderThread.postMessage({route: "page_visibility", hidden: hidden});
    },

    setVideoSuspended(suspended){
        this.decoderThread && this.decoderThread.postMessage({route: "set_video_suspended", suspended: suspended});
    },

    // -- iOS interruption recovery (MediaLifecycle): re-establish the sockets the OS dropped
    reconnectRelays(){
        this.decoderThread && this.decoderThread.postMessage({route: "reconnect_relays"});
    },

    networkOnline(){
        this.decoderThread && this.decoderThread.postMessage({route: "network_online"});
    },

    toggleDTSMute(end_state){
        this.decoderThread && this.decoderThread.postMessage({route: "toggle_dts_mute", state: end_state});
    },

    setTalentLevel(id, value){
        this.decoderThread && this.decoderThread.postMessage({route: "set_talent_level", id: id, value: value});
    },

    setTalentRouting(id, routing){
        this.decoderThread && this.decoderThread.postMessage({route: "set_talent_routing", id: id, routing: routing});
    },

    // -- DuplicateEngAlert's "Use this tab instead": reload and take the room's eng slot
    takeOverEng(){
        try { sessionStorage.setItem(ENG_TAKEOVER_KEY, "1"); }
        catch { history.replaceState(null, "", ENG_TAKEOVER_HASH); }
        Logger.log("Page Load", "info", "App Reload", {reason: "eng-takeover"});
        Logger.flush(true);
        window.location.reload();
    },

    cleanUpDecoderThread(){
        this.decoderThread && this.decoderThread.postMessage({route: "cleanup_core"});
    },

    // -- push queued log batches out of the worker Loggers; called when the tab goes hidden
    // -- (the last reliable moment before a mobile background kill)
    flushWorkerLogs(){
        this.decoderThread && this.decoderThread.postMessage({route: "flush-logs"});
    },

    initDecoderThread(room_name, peer_type, awp_port){

        decoder_thread_ready.set(false);
        this.decoderThread = new Worker(new URL('./Core/DecoderIO.js', import.meta.url), {name: `${peer_type}-decoder`, type: 'module'})

        this.decoderThread.onmessage = e => {
            
            if (e.data.route){

                const msg = e.data;

                switch (msg.route){

                    case "new-self-peer":
                        Peers_Handler.set_self_id(msg.id);
                        core_init_ok.set(true);
                        console.log("[PL-Dispatcher] Core and Self Peer Fully Init:", msg.id);

                        Logger.user_id = msg.id;
                        Logger.user_type = OutputConfig.session.self_type;
                        Logger.log("Relays", "info", "Connected To Relays", get(connection_status));
                        break;

                    // -- the worker's first boot threw (DecoderCallbacks) and it dropped its links:
                    // -- new-self-peer will never come, so the start panel stops waiting on it
                    case "core-init-failed":
                        Logger.user_id = msg.id;
                        Logger.log("Errors", "error", "Core Init Failed", {error: msg.error, stack: msg.stack});
                        core_init_failed.set(true);
                        break;

                    case "new-peers":
                        Peers_Handler.add_remote_peers(msg.peers, OutputConfig.session.room_name);
                        break;

                    // -- the signaling ws reconnected and the worker finished its room re-sync;
                    // -- replay the self state that only lives in main-thread stores (both are
                    // -- no-ops pre-join: the name is empty and can_render_tile is false)
                    case "signaling-resynced": {
                        const self = Peers_Handler.self_peer;
                        const name = get(self.name);
                        if (name) Dispatcher.notifyNameChange(self.id, name);
                        if (get(self.can_render_tile)) Dispatcher.notifyCanRender(self.id, true);
                        Logger.log("Relays", "info", "Signaling Resynced", {replayed_name: !!name});
                        break;
                    }

                    // -- reconnect is permanently off (the server re-minted our id); the worker
                    // -- severed the mis-bound socket (stop_reconnect), so connection-status
                    // -- arrives as {ws:false, gave_up:true} and the existing !ws banner /
                    // -- reload overlay takes it from there
                    case "reconnect-failed":
                        Logger.log("Relays", "error", "Reconnect Failed", {reason: msg.reason});
                        break;

                    // -- pl-ws allows one eng per room. A tab that never booted was refused: show the
                    // -- modal. A booted one lost the room to a newer tab (or was refused on a
                    // -- reconnect): reload, which also leaves the conference, and land on the modal
                    case "eng-conflict":
                        Logger.log("Session", "warn", "Eng Conflict", {code: msg.code, booted: msg.booted});
                        if (msg.booted) {
                            Logger.log("Page Load", "info", "App Reload", {reason: msg.code === 4010 ? "eng-replaced" : "eng-taken"});
                            Logger.flush(true);
                            window.location.reload();
                        }
                        else duplicateEngAlert.set(true);
                        break;

                    case "peer-leaving":
                        Peers_Handler.remove_peer_from_conf_mixer(msg.id);
                        Peers_Handler.remove_peer_from_stream_mixer(msg.id);
                        Peers_Handler.remove_remote_peer(msg.id);
                        Dispatcher._remove_dts_canvas(msg.id);
                        break;

                    case "notify-can-render":
                        Peers_Handler.set_can_render_tile(msg.data.id, msg.data.value);
                        break;

                    case "playback":
                        playback.set(e.data.value);
                        break;

                    // -- eng video decoder is dropping deltas while it waits for a keyframe to
                    // -- resync (audio still plays); drives the playback-canvas loader overlay
                    case "awaiting-keyframe":
                        awaiting_keyframe.set(e.data.value);
                        break;

                    // -- worker reports a MOQ relay / pl-ws drop or restore (drives connection banner)
                    case "connection-status": {
                        const prev = get(connection_status);
                        const s = e.data.status;

                        // -- the worker can post the same status twice (e.g. ws onerror + onclose);
                        // -- only log actual transitions. Degraded means the same condition the
                        // -- banner uses: ws down, or moq down while it's the transport in use.
                        if (prev?.ws !== s.ws || prev?.moq !== s.moq || prev?.moq_in_use !== s.moq_in_use
                            || prev?.reconnecting !== s.reconnecting || prev?.gave_up !== s.gave_up
                            || prev?.moq_transport !== s.moq_transport){
                            const degraded = !s.ws || (s.moq_in_use && !s.moq);
                            Logger.log("Relays", degraded ? "warn" : "info", "Connection Status Changed", s);
                        }

                        connection_status.set(s);
                        break;
                    }
                    
                    // -- originates from awp
                    case "add-stream-mixer-source":
                        Mixer.addStreamSource(e.data.id, e.data.output_num);
                        break;

                    case "pl_version":

                        OutputConfig.plugin_version = e.data.data;
                        OutputConfig.is_apple_silicon = e.data.is_apple_silicon;
                        if (OutputConfig.plugin_version !== BuildSettings.IDEAL_PLUGIN_VERSION)
                            pluginUpdateAvailable.set(true);

                        break;
                        
                    case "set-display-name":
                        Peers_Handler.update_remote_name(msg.data.id, msg.data.name);
                        break;

                    // -- unused but keeping for now
                    // case "abr-metrics":
                    //     ABRController.onMetrics(e.data.data);
                    //     break;

                    // case "abr-underrun":
                    //     ABRController.onUnderrun(e.data.data);
                    //     break;

                }

            }
    
        };

        this.decoderThread.onmessageerror = (event) => {
            console.error(event);
        }

        // -- uncaught worker exceptions were previously invisible remotely (capped: a crash
        // -- loop must not flood the lambda)
        this.decoderThread.onerror = (event) => {
            console.error(event);
            this._worker_errors = (this._worker_errors ?? 0) + 1;
            if (this._worker_errors <= 5)
                Logger.log("Errors", "error", "Decoder Worker Error", {error: event.message, source: `${event.filename ?? ""}:${event.lineno ?? ""}`});
        }

        // -- pl-ws types the peer from the ws URL's t param and keys the room off the ws URL
        //    path (utils.js initPeer), not from init_core's fields. The lambda tolerates
        //    damaged links (t=clients, /my-room%E2%80%8B), so forward the normalized values
        //    it resolved -- passing location.pathname/search raw would broadcast the damaged
        //    type literal in new-peer and strand the peer in a differently-named pl-ws room
        const signaling_params = new URLSearchParams(window.location.search);
        signaling_params.set('t', peer_type);
        // -- eng slot claim params come only from this tab (below), never from the page URL
        ["takeover", "prev", "eng_slot"].forEach(k => signaling_params.delete(k));

        let takeover = false, prev_ids = [];
        if (peer_type === "eng") {

            signaling_params.set('eng_slot', '1'); // -- we handle pl-ws's 4009/4010 (see Relays_v2)

            if (location.hash === ENG_TAKEOVER_HASH) {
                takeover = true;
                history.replaceState(null, "", location.pathname + location.search);
            }
            try {
                takeover = takeover || sessionStorage.getItem(ENG_TAKEOVER_KEY) === "1";
                sessionStorage.removeItem(ENG_TAKEOVER_KEY);
                prev_ids = (sessionStorage.getItem(ENG_PREV_KEY) ?? "").split(",").filter(id => /^[0-9a-f]{8}$/.test(id));
                sessionStorage.removeItem(ENG_PREV_KEY);
            } catch { /* site storage blocked */ }

            // -- not on a bfcache entry: that page may come back with its id. A page that hasn't
            // -- confirmed it got in may or may not hold the slot, so it hands on the inherited ids too
            window.addEventListener("pagehide", e => {
                if (e.persisted) return;
                const ids = get(core_init_ok) ? [this.self_id] : [...prev_ids, this.self_id].slice(-4);
                try { sessionStorage.setItem(ENG_PREV_KEY, ids.join(",")); } catch { /* blocked */ }
            });
        }

        // -- we mint our own peer id: pl-ws adopts it (initPeer) and, because it rides the ws
        // -- URL, a reconnect re-binds our existing peer instead of minting a ghost. 8 hex chars
        // -- to fit the 12-byte stream-header id field.
        this.self_id = Array.from(crypto.getRandomValues(new Uint8Array(4)), b => b.toString(16).padStart(2, "0")).join("");
        signaling_params.set('id', this.self_id);

        this.decoderThread.postMessage({route: "init_core",
            oc_session: OutputConfig.session,
            type: peer_type,
            self_id: this.self_id,
            room_name: room_name,
            url_path: '/' + room_name,
            url_params: '?' + signaling_params.toString(),
            takeover: takeover,
            prev_id: prev_ids.join(",") || null,
            awp_port: awp_port,
            use_expanded_io: peer_type === "eng",
            // -- primes the worker's own Logger copy with this page load's identity
            logger: Logger.identity()
        }, [awp_port]);

        decoder_thread_ready.set(true);

        // -- replay a latency measured before this worker existed (init_core is queued first, so
        // -- Core.init_AWP runs before this lands)
        if (this._last_ahl !== null)
            this.notifyAudioHardwareLatency(this._last_ahl);

        // -- the tap won the race: deliver the resume notification the .then couldn't
        this._notifyCtxResumed();
    },

    // -- `Device To Stream` W I P -- Work In Progress --- UNDER CONSTRUCTION
    async initDTS() {

            if (Peers_Handler.self_peer.type !== "talent")
                return;

            const audio_device = Peers_Handler.self_peer.selected_devices.microphone;
            const video_device = Peers_Handler.self_peer.selected_devices.camera;

            // -- TO DO TO DO -- come back to expanded_io
                // (!get(Peers_Handler.self_peer.use_expanded_io) || !Peers_Handler.self_peer.selected_devices.stream_io.label || Peers_Handler.self_peer.selected_devices.stream_io.label.includes("Postlink")) ?
                // Peers_Handler.self_peer.selected_devices.microphone : Peers_Handler.self_peer.selected_devices.stream_io

            // -- TO DO TO DO
            // -- we need a UI element here to select the channel (1 or 2)
            const result = await DTSInterface.init(audio_device.id, video_device?.id, 1);

            if (!result)
                return;

            const { audio_sab, notify_sab, channels, video_stream, video_track } = result;

            const { port1, port2 } = new MessageChannel();

            console.assert(this.decoderThread);
            this.decoderThread.postMessage({ route: 'register_dts_port', port: port1 }, [port1]);

            const dtsWorker = new Worker(new URL('./DTSWorker.js', import.meta.url), { type: 'module' });
            const init = {
                route:      'init',
                id:         Peers_Handler.self_peer.id,
                audio_sab,
                notify_sab,
                channels,
                port:       port2,
            };
            const transfer = [port2];
            if (video_stream) {
                // Chrome: readable already created on main thread, transfer it
                init.video_stream = video_stream;
                transfer.push(video_stream);
            } else if (video_track) {
                // Safari: MSTP is worker-only, transfer the track and create the processor in DTSWorker
                init.video_track = video_track;
                transfer.push(video_track);
            }
            dtsWorker.postMessage(init, transfer);

    },
    
    _remove_dts_canvas(id) {
        this.decoderThread && this.decoderThread.postMessage({ route: 'remove_dts_canvas', id });
    },

}
